import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Loader2, ShieldQuestion, X } from 'lucide-react';
import { Button } from '../Button';
import { UserProfile } from '../../types';
import {
    ProjectDeletionRequest,
    istLoeschAdmin,
    meinePruefungen,
} from '../../services/projectDeletionLogic';
import {
    LOESCHANTRAEGE_GEAENDERT,
    entscheideProjektLoeschung,
    ladeMeineOffenenPruefungen,
    meldeLoeschantraegeGeaendert,
} from '../../services/projectDeletionService';

/** So oft fragt das Popup nach neuen Antraegen, solange die App offen ist. */
const ABFRAGE_TAKT_MS = 60_000;
/** So oft wird gegen die Uhr geprueft, ob der gezeigte Antrag abgelaufen ist. */
const UHR_TAKT_MS = 30_000;

const datum = (iso?: string | null, mitZeit = true): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return mitZeit
        ? d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
        : d.toLocaleDateString('de-DE');
};

interface Props {
    userProfile: UserProfile | null;
}

/**
 * Pflicht-Popup fuer den ausgelosten Pruefer eines Loeschantrags
 * (Vier-Augen-Prinzip, supabase/project_deletion_approvals.sql, Nr. 49).
 *
 * Bisher stand der Antrag nur als Glockeneintrag und im Reiter
 * „Löschanträge" — wer dort nicht nachsah, liess ihn ablaufen. Jetzt legt
 * sich das Popup ueber die ganze App, sobald ein Antrag auf DIESEN Nutzer
 * wartet, und geht erst weg, wenn er freigegeben oder abgelehnt ist.
 * Bewusst ohne „Später"/Schliessen: der Betreiber will, dass sofort
 * entschieden wird.
 *
 * Abgefragt wird beim Start, jede Minute, beim Zurueckkehren in den Tab und
 * auf das Signal `LOESCHANTRAEGE_GEAENDERT`. Fehler beim Laden sind still
 * (fail-open): das Popup darf die App nie blockieren, weil eine Abfrage
 * klemmt — es blockiert nur, wenn ein Antrag sicher wartet.
 */
export const LoeschantragPruefDialog: React.FC<Props> = ({ userProfile }) => {
    const userId = userProfile?.id || null;
    const role = userProfile?.role || null;
    const aktiv = istLoeschAdmin({ id: userId, role });

    const [antraege, setAntraege] = useState<ProjectDeletionRequest[]>([]);
    // Uhr fuer den Ablauf, mit eigenem Takt und unabhaengig vom Netz: ein
    // Antrag, der waehrend einer haengenden Abfrage ablaeuft, verschwindet trotzdem.
    const [jetzt, setJetzt] = useState(() => new Date());
    const [beschaeftigt, setBeschaeftigt] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [ablehnen, setAblehnen] = useState(false);
    const [notiz, setNotiz] = useState('');
    // Generation des Ladestands. Nutzerwechsel und gelungene Entscheidung
    // ziehen sie hoch: eine Antwort, die davor losgeschickt wurde, ist dann
    // veraltet und wird verworfen.
    const generationRef = useRef(0);
    // Je Generation hoechstens EINE Abfrage unterwegs. Wer waehrenddessen
    // laden will, merkt das Nachladen vor — sonst verwuerfen sich langsame,
    // ueberlappende Abfragen gegenseitig und nie kaeme ein Stand an.
    const unterwegsRef = useRef<number | null>(null);
    const nachladenRef = useRef(false);
    // Gerade entschiedene Antraege. Eine Antwort, die den Stand noch vor der
    // Entscheidung gelesen hat, soll sie nicht zurueckbringen.
    const entschiedenRef = useRef<Set<string>>(new Set());

    const laden = useCallback(async () => {
        if (!aktiv || !userId) {
            setAntraege([]);
            return;
        }
        const generation = generationRef.current;
        if (unterwegsRef.current === generation) {
            nachladenRef.current = true;
            return;
        }
        unterwegsRef.current = generation;
        try {
            do {
                nachladenRef.current = false;
                try {
                    const daten = await ladeMeineOffenenPruefungen(userId);
                    if (generation !== generationRef.current) return;
                    setAntraege(daten.filter((r) => !entschiedenRef.current.has(r.id)));
                } catch {
                    if (generation !== generationRef.current) return;
                    // fail-open, siehe Kopf: ohne gesicherten Stand blockiert
                    // das Popup nicht, auch wenn es eben noch einen Antrag zeigte.
                    setAntraege([]);
                }
            } while (nachladenRef.current);
        } finally {
            if (unterwegsRef.current === generation) unterwegsRef.current = null;
        }
    }, [aktiv, userId]);

    // Anderer Nutzer oder andere Rolle: alles Geladene gehoert dem alten.
    useEffect(() => {
        generationRef.current += 1;
        nachladenRef.current = false;
        entschiedenRef.current = new Set();
        setAntraege([]);
    }, [userId, role]);

    useEffect(() => {
        if (!aktiv) return;
        const uhr = window.setInterval(() => setJetzt(new Date()), UHR_TAKT_MS);
        return () => window.clearInterval(uhr);
    }, [aktiv]);

    useEffect(() => {
        if (!aktiv) {
            setAntraege([]);
            return;
        }
        void laden();
        const takt = window.setInterval(() => { void laden(); }, ABFRAGE_TAKT_MS);
        const beiSichtbar = () => { if (document.visibilityState === 'visible') void laden(); };
        const beiAenderung = () => { void laden(); };
        document.addEventListener('visibilitychange', beiSichtbar);
        window.addEventListener(LOESCHANTRAEGE_GEAENDERT, beiAenderung);
        return () => {
            window.clearInterval(takt);
            document.removeEventListener('visibilitychange', beiSichtbar);
            window.removeEventListener(LOESCHANTRAEGE_GEAENDERT, beiAenderung);
        };
    }, [aktiv, laden]);

    // Beim Anzeigen noch einmal gegen die AKTUELLE Identitaet und Uhr gefiltert:
    // was geladen wurde, ist nur die Vorauswahl.
    const offen = aktiv && userId ? meinePruefungen(antraege, { id: userId, role }, jetzt) : [];
    const aktuell = offen[0] || null;
    const aktuellId = aktuell?.id || null;

    // Neuer Antrag vorn: keine halb getippte Ablehnung vom vorigen mitnehmen.
    useEffect(() => {
        setAblehnen(false);
        setNotiz('');
        setFehler(null);
    }, [aktuellId]);

    if (!aktuell) return null;

    const entscheiden = async (freigeben: boolean) => {
        setBeschaeftigt(true);
        setFehler(null);
        try {
            await entscheideProjektLoeschung(aktuell.id, freigeben, freigeben ? undefined : notiz);
            // Laufende Abfragen verwerfen, den Antrag merken, dann ueber das
            // Signal sicher neu laden (Popup und Verwaltungsreiter).
            generationRef.current += 1;
            nachladenRef.current = false;
            entschiedenRef.current.add(aktuell.id);
            setAntraege((alt) => alt.filter((r) => r.id !== aktuell.id));
            meldeLoeschantraegeGeaendert();
        } catch (e: unknown) {
            // Meist ist der Antrag inzwischen zurueckgezogen oder abgelaufen:
            // Meldung zeigen und neu laden, dann verschwindet er von selbst.
            setFehler((e instanceof Error && e.message) || 'Die Entscheidung konnte nicht gespeichert werden.');
            void laden();
        } finally {
            setBeschaeftigt(false);
        }
    };

    const weitere = offen.length - 1;

    return (
        <div
            className="fixed inset-0 z-[250] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="loeschantrag-pruefen-titel"
            aria-describedby="loeschantrag-pruefen-text"
        >
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
                <h3 id="loeschantrag-pruefen-titel" className="font-bold text-lg text-slate-800 flex items-center gap-2">
                    <ShieldQuestion className="w-5 h-5 text-amber-600 shrink-0" /> Löschantrag prüfen
                </h3>
                <p id="loeschantrag-pruefen-text" className="text-sm text-slate-600 mt-2">
                    Sie wurden als zweiter Administrator ausgelost (Vier-Augen-Prinzip). Bitte jetzt
                    entscheiden — ohne Entscheidung läuft der Antrag am {datum(aktuell.expires_at, false)} ab.
                </p>

                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <p className="font-bold text-slate-800 break-words">
                        {aktuell.project_number ? <span className="text-slate-500 mr-2">{aktuell.project_number}</span> : null}
                        {aktuell.project_name}
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                        Beantragt von <strong>{aktuell.requested_by_name}</strong> am {datum(aktuell.created_at)}
                    </p>
                </div>

                <p className="mt-3 text-xs font-bold text-slate-500">Begründung</p>
                <p className="mt-1 text-sm text-slate-700 bg-slate-50 rounded-xl p-3 whitespace-pre-wrap break-words">
                    {aktuell.reason}
                </p>

                <p className="mt-3 text-xs text-slate-500">
                    Freigeben löscht noch nichts: danach kann das Projekt unter Verwaltung → Projektverwaltung → Löschanträge
                    endgültig gelöscht werden, vorher wird eine vollständige Sicherung angelegt.
                </p>

                {fehler && (
                    <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 flex gap-2">
                        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-rose-800 leading-relaxed">{fehler}</p>
                    </div>
                )}

                {ablehnen ? (
                    <div className="mt-4">
                        <textarea
                            autoFocus
                            rows={3}
                            value={notiz}
                            onChange={(e) => setNotiz(e.target.value)}
                            placeholder="Warum lehnen Sie ab? (geht an den Antragsteller)"
                            className="w-full rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 resize-none"
                        />
                        <div className="flex flex-wrap justify-end gap-2 mt-2">
                            <Button variant="ghost" size="sm" onClick={() => setAblehnen(false)} disabled={beschaeftigt}>
                                Zurück
                            </Button>
                            <Button
                                variant="danger"
                                size="sm"
                                disabled={beschaeftigt}
                                onClick={() => void entscheiden(false)}
                                icon={beschaeftigt ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                            >
                                Ablehnung senden
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="mt-5 flex flex-wrap justify-end gap-2">
                        <Button
                            variant="secondary"
                            disabled={beschaeftigt}
                            onClick={() => setAblehnen(true)}
                            icon={<X className="w-4 h-4" />}
                        >
                            Ablehnen
                        </Button>
                        <Button
                            variant="primary"
                            disabled={beschaeftigt}
                            onClick={() => void entscheiden(true)}
                            icon={beschaeftigt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        >
                            Freigeben
                        </Button>
                    </div>
                )}

                {weitere > 0 && (
                    <p className="text-xs text-slate-400 mt-3 text-right">
                        Danach {weitere === 1 ? 'wartet noch 1 weiterer Antrag' : `warten noch ${weitere} weitere Anträge`}.
                    </p>
                )}
            </div>
        </div>
    );
};
